import { useQueryClient } from '@tanstack/react-query';
import {
  useGetBulkReprocessRun,
  useRetryBulkReprocessRun,
  useCancelBulkReprocessRun,
  getGetBulkReprocessRunQueryKey,
  getListBulkReprocessRunsQueryKey,
} from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { Skeleton } from '@sara/ui/components/skeleton';
import { toast } from '@sara/ui/components/sonner';
import { ArrowLeft, ListRestart, Square } from 'lucide-react';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { MODE_METADATA, RUN_STATUS_VARIANT } from './display';

interface BulkReprocessRunDetailProps {
  runId: string;
  onBack: () => void;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone ?? 'text-foreground'}`}>
        {value}
      </p>
    </div>
  );
}

export function BulkReprocessRunDetail({ runId, onBack }: BulkReprocessRunDetailProps) {
  const queryClient = useQueryClient();

  const {
    data: run,
    isLoading,
    error,
  } = useGetBulkReprocessRun(runId, {
    query: {
      refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? 3000 : false),
    },
  });

  function invalidateRun() {
    void queryClient.invalidateQueries({ queryKey: getGetBulkReprocessRunQueryKey(runId) });
    void queryClient.invalidateQueries({ queryKey: getListBulkReprocessRunsQueryKey() });
  }

  const { mutate: retry, isPending: isRetrying } = useRetryBulkReprocessRun({
    mutation: {
      onSuccess: () => {
        toast.success('Retrying failed items');
        invalidateRun();
      },
      onError: (err) =>
        toast.error('Could not retry the run', { description: getApiErrorMessage(err) }),
    },
  });

  const { mutate: cancel, isPending: isCanceling } = useCancelBulkReprocessRun({
    mutation: {
      onSuccess: () => {
        toast.success('Run canceled');
        invalidateRun();
      },
      onError: (err) =>
        toast.error('Could not cancel the run', { description: getApiErrorMessage(err) }),
    },
  });

  return (
    <section className="space-y-5">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="gap-1.5 pl-0">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.7} />
        Back to runs
      </Button>

      {isLoading && (
        <div className="grid gap-3" data-testid="bulk-reprocess-run-detail-loading">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[120px] rounded-lg" />
        </div>
      )}

      {error ? (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {getApiErrorMessage(error) ?? 'Failed to load this run'}
        </div>
      ) : null}

      {run && (
        <div className="space-y-5 rounded-xl border border-border bg-card p-5">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="font-mono text-sm text-muted-foreground">{run.id}</h2>
                <Badge variant={RUN_STATUS_VARIANT[run.status]}>{run.status}</Badge>
              </div>
              <p className="text-sm text-foreground">{MODE_METADATA[run.mode].label}</p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={run.status === 'RUNNING' || run.failedCount === 0 || isRetrying}
                onClick={() => retry({ id: run.id })}
              >
                <ListRestart className="h-3.5 w-3.5" strokeWidth={1.7} />
                Retry failed
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={(run.status !== 'RUNNING' && run.status !== 'PENDING') || isCanceling}
                onClick={() => cancel({ id: run.id })}
              >
                <Square className="h-3.5 w-3.5" strokeWidth={1.7} />
                Cancel
              </Button>
            </div>
          </header>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total" value={run.totalCount} />
            <StatCard label="Succeeded" value={run.succeededCount} tone="text-success" />
            <StatCard label="Failed" value={run.failedCount} tone="text-destructive" />
            <StatCard
              label="Remaining"
              value={Math.max(0, run.totalCount - run.succeededCount - run.failedCount)}
            />
          </div>

          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="text-foreground">{formatDate(run.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Started</dt>
              <dd className="text-foreground">{formatDate(run.startedAt)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Finished</dt>
              <dd className="text-foreground">{formatDate(run.finishedAt)}</dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  );
}
